/**
 * A remote exam session for signed-in users.
 *   Room computer (the tech who started it): the call, the join QR for a phone near the patient, take the room audio
 *     back, remove a device, stop the session.
 *   Remote reader (the doctor): join, the call with the patient panel beside it, leave.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Aperture,
  ArrowSquareOut,
  Desktop,
  DeviceMobile,
  QrCode,
  SignOut,
  Stop,
  UserCircle,
  VideoCamera,
} from "@phosphor-icons/react";
import { ConnectionDots, Mono, SectionLabel, StatusChip, VideoFrame } from "../ui";
import QRCode from "qrcode";
import { http, RECORDING_QUALITIES } from "../api";
import { useSession } from "../AuthToken";
import { SessionCall, SessionState } from "../SessionCall";
import { RecordingPlayer } from "../RecordingPlayer";

interface Pass {
  url: string;
  expiresAt: string;
  role?: "room" | "reader";
}

/** A join code for one role: the room phone near the patient, or the doctor's invite (joins as remote reader). */
function JoinQr({
  sessionId,
  token,
  phoneJoined,
  role = "room",
}: {
  sessionId: string;
  token: string;
  phoneJoined: boolean;
  role?: "room" | "reader";
}) {
  const reader = role === "reader";
  const [copied, setCopied] = useState(false);
  const [pass, setPass] = useState<Pass | null>(() => {
    try {
      const p = JSON.parse(sessionStorage.getItem(`pass:${role}:${sessionId}`) ?? "null") as Pass | null;
      return p && new Date(p.expiresAt).getTime() > Date.now() ? p : null;
    } catch {
      return null;
    }
  });
  const [img, setImg] = useState("");
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState("");
  const issue = async () => {
    const r = await http.post<Pass>(token, `/sessions/${sessionId}/pass`, { role });
    if (!r.ok) return setError(r.error);
    setPass(r.result);
    try {
      sessionStorage.setItem(`pass:${role}:${sessionId}`, JSON.stringify(r.result));
    } catch {
      /* fine */
    }
  };
  useEffect(() => {
    if (!pass) return;
    QRCode.toDataURL(pass.url, { margin: 1, width: 240, color: { dark: "#0b0c0e", light: "#f3efe8" } }).then(setImg);
  }, [pass]);
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const left = pass ? Math.max(0, new Date(pass.expiresAt).getTime() - now) : 0;
  return (
    <Stack spacing={1.5} alignItems="center">
      <SectionLabel icon={<QrCode size={16} />}>{reader ? "Invite a doctor" : "Room phone"}</SectionLabel>
      <Typography variant="body2" color="text.secondary" sx={{ alignSelf: "flex-start" }}>
        {reader
          ? "The doctor scans this code, or opens the link, and joins as the remote reader with camera and mic. No login needed; it works once."
          : "Scan the code with a phone near the patient."}
      </Typography>
      {pass && left > 0 ? (
        <>
          {img && (
            <Box
              sx={{
                p: 1.25,
                borderRadius: "14px",
                bgcolor: "#f3efe8",
                boxShadow: "0 0 0 1px rgba(217,162,95,0.35), 0 0 40px rgba(217,162,95,0.12)",
              }}
            >
              <img src={img} alt="Scan to join this session" width={200} height={200} style={{ display: "block" }} />
            </Box>
          )}
          <ConnectionDots
            state={phoneJoined ? "live" : "connecting"}
            from={<QrCode size={16} />}
            to={<DeviceMobile size={16} />}
            label={
              phoneJoined
                ? reader
                  ? "the doctor has joined"
                  : "a phone has joined"
                : reader
                ? "waiting for the doctor"
                : "waiting for a phone to scan"
            }
          />
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
            Expires in{" "}
            <Mono>
              {Math.floor(left / 60000)}:{String(Math.floor((left % 60000) / 1000)).padStart(2, "0")}
            </Mono>
            .{" "}
            {reader
              ? "Send the link by text or chat if the doctor is not in the room."
              : "The phone takes over room audio and camera."}
          </Typography>
          <Stack direction="row" spacing={1}>
            {reader && (
              <Button
                size="small"
                onClick={() => {
                  navigator.clipboard?.writeText(pass.url).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  });
                }}
              >
                {copied ? "Link copied" : "Copy link"}
              </Button>
            )}
            <Button size="small" onClick={issue}>
              New code
            </Button>
          </Stack>
        </>
      ) : (
        <Button variant="outlined" fullWidth startIcon={<QrCode size={16} />} onClick={issue}>
          {reader ? "Invite a doctor" : "Show join code"}
        </Button>
      )}
      {error && (
        <Typography color="error" variant="caption">
          {error}
        </Typography>
      )}
    </Stack>
  );
}

export default function SessionView() {
  const { id } = useParams();
  const { token, me } = useSession();
  const navigate = useNavigate();
  const [state, setState] = useState<
    | (SessionState & {
        orderId?: string;
        recordingQuality?: string;
        recording?: { status: string; id: string | null; error: string | null } | null;
      })
    | null
  >(null);
  const [error, setError] = useState("");

  const load = async () => {
    const r = await http.get<SessionState>(token, `/sessions/${id}/state`);
    if (r.ok) {
      setState(r.result);
      setError("");
    } else setError(r.error);
  };
  useEffect(() => {
    if (!token) return;
    load();
    const iv = setInterval(load, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, id]);

  const act = async (path: string, body: unknown = {}) => {
    const r = await http.post<unknown>(token, `/sessions/${id}${path}`, body);
    if (!r.ok) setError(r.error);
    await load();
  };

  if (!state) return error ? <Alert severity="error">{error}</Alert> : null;
  const live = state.status === "live";
  const isRoom = state.me?.kind === "room";
  const canJoinRemote = !state.me && live && (me?.role === "reader" || me?.role === "admin");
  const canJoinRoom = !state.me && live && (me?.role === "tech" || me?.role === "admin");
  const full = state.patient.full;

  // Another connected room device is the phone that scanned the code.
  const phoneJoined = state.participants.some((p) => p.kind === "room" && !p.isMe && p.connected);
  const doctorJoined = state.participants.some((p) => p.kind === "remote" && !p.isMe && p.connected);
  const partIcon = (kind: string, device: string | null) =>
    kind === "room" ? (
      device && /phone|mobile|ios|android/i.test(device) ? (
        <DeviceMobile size={18} />
      ) : (
        <Desktop size={18} />
      )
    ) : (
      <UserCircle size={18} />
    );

  return (
    <Stack direction={{ xs: "column", lg: "row" }} spacing={3} alignItems="flex-start">
      <Box sx={{ flexGrow: 1, width: "100%", minWidth: 0 }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        {!live && (
          <Paper sx={{ p: 2.5, mb: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: state.recording?.id ? 2 : 0 }}>
              <Typography sx={{ flexGrow: 1 }}>
                Session ended{state.endedAt ? ` at ${new Date(state.endedAt).toLocaleTimeString()}` : ""}.{" "}
                {state.recording?.id
                  ? "The recording (instrument video and audio from all participants) is saved on the order."
                  : state.recording?.status === "failed"
                  ? `Recording failed: ${state.recording.error ?? "unknown error"}. Open the order for details.`
                  : state.recording?.status === "empty"
                  ? "Nothing was recorded."
                  : "Preparing the recording (instrument video and audio from all participants). This usually takes under a minute."}
              </Typography>
              {state.orderId && (
                <Button
                  variant="outlined"
                  endIcon={<ArrowSquareOut size={14} />}
                  onClick={() => navigate(`/orders/${state.orderId}`)}
                >
                  Open order
                </Button>
              )}
            </Stack>
            {state.recording?.id && <RecordingPlayer recordingId={state.recording.id} token={token} />}
          </Paper>
        )}
        {live && state.me ? (
          <SessionCall state={state} token={token} onTakeRoomAudio={() => act("/room-audio")} />
        ) : !live ? null : (
          <VideoFrame aspect="16 / 9">
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={2.5}
              sx={{ position: "absolute", inset: 0, p: 3, textAlign: "center" }}
            >
              <ConnectionDots
                state="waiting"
                from={<Aperture size={22} />}
                to={<UserCircle size={22} />}
                dots={9}
                size={5}
              />
              <Typography sx={{ maxWidth: 420 }}>
                To view the {state.instrument.label} and speak with the room, join the session.
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                {canJoinRemote && (
                  <Button
                    variant="contained"
                    startIcon={<VideoCamera size={16} weight="regular" />}
                    onClick={() => act("/join", { as: "remote" })}
                  >
                    Join as remote reader
                  </Button>
                )}
                {canJoinRoom && (
                  <Button
                    variant="outlined"
                    startIcon={<Desktop size={16} />}
                    onClick={() => act("/join", { as: "room" })}
                  >
                    Join as room device
                  </Button>
                )}
              </Stack>
            </Stack>
          </VideoFrame>
        )}
      </Box>
      <Paper sx={{ p: 2.5, width: { xs: "100%", lg: 340 }, flexShrink: 0 }}>
        <Stack spacing={2}>
          <Box>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="overline" sx={{ color: "primary.main", flexGrow: 1 }}>
                Patient
              </Typography>
              <StatusChip status={state.status} />
            </Stack>
            <Typography variant="h6" sx={{ fontSize: 20 }}>
              {full?.name ?? state.patient.label}
            </Typography>
            {full && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.7 }}>
                MRN <Mono sx={{ color: "text.primary" }}>{full.mrn}</Mono>
                {full.birthDate ? ` · DOB ${full.birthDate}` : ""}
                <br />
                Accession <Mono sx={{ color: "primary.light" }}>{full.accession}</Mono>
                <br />
                {full.procedure}
              </Typography>
            )}
          </Box>
          <Divider />
          <Box>
            <SectionLabel count={state.participants.length + 1}>Participants</SectionLabel>
            <List dense disablePadding sx={{ mt: 0.5 }}>
              <ListItem disableGutters>
                <ListItemIcon sx={{ minWidth: 30, color: "primary.main" }}>
                  <Aperture size={18} />
                </ListItemIcon>
                <ListItemText primary={state.instrument.label} secondary="instrument · video only" />
              </ListItem>
              {state.participants.map((p) => (
                <ListItem
                  key={p.id}
                  disableGutters
                  sx={{
                    alignItems: "flex-start",
                    pr: live && !p.isMe && (state.canControl || state.me?.kind === "remote") ? 9 : 0,
                  }}
                  secondaryAction={
                    live && !p.isMe && (state.canControl || state.me?.kind === "remote") ? (
                      <Button size="small" color="error" onClick={() => act(`/participants/${p.id}/remove`)}>
                        Remove
                      </Button>
                    ) : undefined
                  }
                >
                  <ListItemIcon
                    sx={{ minWidth: 30, mt: 0.75, color: p.connected ? "secondary.main" : "text.disabled" }}
                  >
                    {partIcon(p.kind, p.device)}
                  </ListItemIcon>
                  <ListItemText
                    primary={`${p.label}${p.isMe ? " (you)" : ""}`}
                    secondary={
                      <Stack component="span" spacing={0.5} sx={{ display: "flex" }}>
                        <span>{`${p.kind}${p.kind === "room" ? (p.audio ? " · has room audio" : " · muted") : ""}${
                          p.connected ? "" : " · disconnected"
                        }`}</span>
                        <ConnectionDots
                          state={!live ? "idle" : p.connected ? "live" : "failed"}
                          dots={5}
                          size={3}
                          label={!live ? "ended" : p.connected ? "connected" : "disconnected"}
                        />
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: "span" }}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
          {live && isRoom && (
            <>
              <Divider />
              <JoinQr sessionId={state.id} token={token} phoneJoined={phoneJoined} />
              <JoinQr sessionId={state.id} token={token} phoneJoined={doctorJoined} role="reader" />
            </>
          )}
          {live && state.canControl && (
            <>
              <Divider />
              <TextField
                size="small"
                select
                label="Recording quality"
                value={state.recordingQuality ?? "economy"}
                onChange={(e) => act("/quality", { quality: e.target.value })}
                helperText="Instrument video is pinned. Each camera is a tile. Audio from all participants is included. Applied when the session stops."
              >
                {RECORDING_QUALITIES.map((q) => (
                  <MenuItem key={q.value} value={q.value}>
                    {q.label}
                  </MenuItem>
                ))}
              </TextField>
            </>
          )}
          {live && (
            <>
              <Divider />
              <Stack spacing={1}>
                {state.me && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    startIcon={<SignOut size={16} />}
                    onClick={() => act("/leave").then(() => navigate("/sessions"))}
                  >
                    Leave
                  </Button>
                )}
                {(state.canControl || isRoom) && (
                  <Button color="error" variant="outlined" startIcon={<Stop size={16} />} onClick={() => act("/stop")}>
                    Stop session
                  </Button>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
